import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Description as DocumentationIcon, Save as SaveIcon } from '@mui/icons-material';
import { SectionHeader } from './Sensors';

const streamRateFields = [
    { name: 'rawSensors', label: 'Raw Sensors' },
    { name: 'extendedStatus', label: 'Extended Status' },
    { name: 'rcChannel', label: 'RC Channel' },
    { name: 'rawCtrl', label: 'Raw Control' },
    { name: 'position', label: 'Position' },
    { name: 'extra1', label: 'Extra 1' },
    { name: 'extra2', label: 'Extra 2' },
    { name: 'extra3', label: 'Extra 3' },
    { name: 'params', label: 'Params' },
    { name: 'adsb', label: 'ADSB' },
];

const gcsRequestRateFields = streamRateFields.filter(({ name }) => (
    ['rawSensors', 'extendedStatus', 'rcChannel', 'position', 'extra1', 'extra2', 'extra3'].includes(name)
));

const streamRateParamSuffixes = {
    rawSensors: 'RAW_SENS',
    extendedStatus: 'EXT_STAT',
    rcChannel: 'RC_CHAN',
    rawCtrl: 'RAW_CTRL',
    position: 'POSITION',
    extra1: 'EXTRA1',
    extra2: 'EXTRA2',
    extra3: 'EXTRA3',
    params: 'PARAMS',
    adsb: 'ADSB',
};

const ardupilotStreamRateMessages = [
    {
        suffix: 'RAW_SENS',
        messages: ['RAW_IMU', 'SCALED_IMU2', 'SCALED_IMU3', 'SCALED_PRESSURE', 'SCALED_PRESSURE2', 'SCALED_PRESSURE3', 'AIRSPEED'],
    },
    {
        suffix: 'EXT_STAT',
        messages: ['SYS_STATUS', 'POWER_STATUS', 'MCU_STATUS', 'MEMINFO', 'CURRENT_WAYPOINT', 'GPS_RAW_INT', 'GPS_RTK', 'GPS2_RAW_INT', 'GPS2_RTK', 'NAV_CONTROLLER_OUTPUT', 'FENCE_STATUS', 'GLOBAL_TARGET_POS_INT'],
    },
    { suffix: 'RC_CHAN', messages: ['SERVO_OUTPUT_RAW', 'RC_CHANNELS'] },
    { suffix: 'RAW_CTRL', messages: ['SERVO_OUT'] },
    { suffix: 'POSITION', messages: ['GLOBAL_POSITION_INT', 'LOCAL_POSITION_NED'] },
    {
        suffix: 'EXTRA1',
        messages: ['ATTITUDE', 'SIMSTATE', 'AHRS2', 'RPM', 'AOA_SSA', 'LANDING', 'ESC_TELEMETRY', 'EFI_STATUS', 'PID_TUNING'],
    },
    { suffix: 'EXTRA2', messages: ['VFR_HUD'] },
    {
        suffix: 'EXTRA3',
        messages: ['AHRS', 'SYSTEM_TIME', 'WIND', 'RANGEFINDER', 'DISTANCE_SENSOR', 'TERRAIN_REQUEST', 'TERRAIN_REPORT', 'GIMBAL_DEVICE_ATTITUDE_STATUS', 'OPTICAL_FLOW', 'MAG_CAL_REPORT', 'MAG_CAL_PROGRESS', 'EKF_STATUS_REPORT', 'VIBRATION', 'BATTERY_STATUS'],
    },
    { suffix: 'PARAMS', messages: ['PARAM_VALUE'] },
    { suffix: 'ADSB', messages: ['ADSB_VEHICLE'] },
];

const defaultStreamRates = {
    rawSensors: 2,
    extendedStatus: 2,
    rcChannel: 2,
    rawCtrl: 0,
    position: 3,
    extra1: 10,
    extra2: 10,
    extra3: 3,
    params: 0,
    adsb: 0,
};

const normalizeStreamRates = (rates) => {
    const out = { ...defaultStreamRates };
    if (!rates || typeof rates !== 'object') return out;
    Object.keys(defaultStreamRates).forEach((key) => {
        const num = Number(rates[key]);
        if (Number.isFinite(num)) out[key] = num;
    });
    return out;
};

const clampStreamRate = (rawValue, fallbackValue = 0) => {
    const num = Number(rawValue);
    if (!Number.isFinite(num)) return Number(fallbackValue) || 0;
    return Math.max(0, Math.min(50, Math.round(num)));
};

const resolveParamValue = (entry) => {
    const raw = entry && typeof entry === 'object' && Object.prototype.hasOwnProperty.call(entry, 'value')
        ? entry.value
        : entry;
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
};

const isMavlinkProtocol = (entry) => {
    const value = resolveParamValue(entry);
    return value === 1 || value === 2;
};

const buildArdupilotChannelDescriptors = (paramMap) => {
    const channels = new Map();
    Object.keys(paramMap || {}).forEach((name) => {
        const srMatch = name.match(/^SR(\d+)_(RAW_SENS|EXT_STAT|RC_CHAN|RAW_CTRL|POSITION|EXTRA1|EXTRA2|EXTRA3|PARAMS|ADSB)$/);
        if (!srMatch) return;
        const id = Number(srMatch[1]);
        if (!channels.has(id)) channels.set(id, new Set());
        const field = Object.keys(streamRateParamSuffixes)
            .find((key) => streamRateParamSuffixes[key] === srMatch[2]);
        if (field) channels.get(id).add(field);
    });
    const descriptors = Array.from(channels.entries())
        .sort(([a], [b]) => a - b)
        .map(([id, fields]) => ({ id: String(id), label: `SR${id}`, fields: Array.from(fields) }));

    const mappedInterfaces = new Set();
    descriptors.forEach((descriptor) => {
        const serialName = `SERIAL${descriptor.id}`;
        if (isMavlinkProtocol(paramMap?.[`${serialName}_PROTOCOL`])) {
            descriptor.interfaceLabel = serialName;
            mappedInterfaces.add(serialName);
        }
    });

    const virtualInterfaces = [];
    Object.keys(paramMap || {}).forEach((name) => {
        if (!isMavlinkProtocol(paramMap[name])) return;
        const netMatch = name.match(/^NET_P(\d+)_PROTOCOL$/);
        if (netMatch) {
            virtualInterfaces.push({ label: `NET_P${netMatch[1]}`, rank: 0, id: Number(netMatch[1]) });
            return;
        }
        const scriptMatch = name.match(/^SCR_SDEV(\d+)_PROTO$/);
        if (scriptMatch) {
            virtualInterfaces.push({ label: `SCR_SDEV${scriptMatch[1]}`, rank: 1, id: Number(scriptMatch[1]) });
            return;
        }
        if (name === 'SCR_SDEV_PROTO') {
            virtualInterfaces.push({ label: 'SCR_SDEV', rank: 1, id: 0 });
            return;
        }
        const canSerialMatch = name.match(/^CAN_D(\d+)_UC_S(\d+)_PRO$/);
        if (canSerialMatch) {
            const driverId = Number(canSerialMatch[1]);
            const serialId = Number(canSerialMatch[2]);
            const prefix = `CAN_D${driverId}_UC`;
            const serialEnabled = resolveParamValue(paramMap[`${prefix}_SER_EN`]);
            const remoteSerialIndex = resolveParamValue(paramMap[`${prefix}_S${serialId}_IDX`]);
            if (serialEnabled === 1 && remoteSerialIndex !== null && remoteSerialIndex >= 0) {
                virtualInterfaces.push({
                    label: `${prefix}_S${serialId}`,
                    rank: 2,
                    id: driverId * 100 + serialId,
                });
            }
        }
    });
    virtualInterfaces.sort((a, b) => a.rank - b.rank || a.id - b.id);

    const unmappedChannels = descriptors.filter((descriptor) => !descriptor.interfaceLabel);
    virtualInterfaces.forEach((virtualInterface, index) => {
        if (!mappedInterfaces.has(virtualInterface.label) && unmappedChannels[index]) {
            unmappedChannels[index].interfaceLabel = virtualInterface.label;
            mappedInterfaces.add(virtualInterface.label);
        }
    });

    return descriptors;
};

const buildArdupilotRateParamUpdates = (channelDescriptors, channelRates, paramMap) => {
    const updates = {};
    channelDescriptors.forEach(({ id }) => {
        const rates = normalizeStreamRates(channelRates[id]);
        Object.entries(streamRateParamSuffixes).forEach(([field, suffix]) => {
            const paramName = `SR${id}_${suffix}`;
            const existing = paramMap[paramName];
            // Never invent SRx parameters: PARAM_SET for an unknown name is ignored by ArduPilot.
            if (!existing || typeof existing !== 'object') return;
            updates[paramName] = {
                value: Number(rates[field]),
                type: existing.type,
            };
        });
    });
    return updates;
};

const mergeArdupilotRatesFromParams = (channelDescriptors, prevRatesMap, paramMap) => {
    const next = { ...prevRatesMap };
    channelDescriptors.forEach(({ id }) => {
        const current = normalizeStreamRates(next[id]);
        const merged = { ...current };
        Object.entries(streamRateParamSuffixes).forEach(([field, suffix]) => {
            const srName = `SR${id}_${suffix}`;
            const paramName = Object.prototype.hasOwnProperty.call(paramMap || {}, srName) ? srName : null;
            const paramValue = paramName ? resolveParamValue(paramMap[paramName]) : null;
            if (paramValue !== null) {
                merged[field] = clampStreamRate(paramValue, current[field]);
            }
        });
        next[id] = merged;
    });
    return next;
};

const Stat = ({ label, value, unit }) => (
    <Box
        sx={{
            bgcolor: 'background.default',
            borderRadius: 1,
            p: 1.25,
            minWidth: 130,
            flex: '1 1 140px',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
        }}
    >
        <Typography
            sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                fontSize: 10,
            }}
        >
            {label}
        </Typography>
        <Typography
            sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                fontVariantNumeric: 'tabular-nums',
                fontSize: 18,
                lineHeight: 1.2,
            }}
        >
            {value}
            {unit && (
                <Typography
                    component="span"
                    sx={{ ml: 0.5, fontSize: 12, color: 'text.secondary' }}
                >
                    {unit}
                </Typography>
            )}
        </Typography>
    </Box>
);

const TelemetryPage = ({ selectedMavId, paramListTimestamp }) => {
    const [mavlinkSettings, setMavlinkSettings] = useState({});
    const [gcsSystemId, setGcsSystemId] = useState(255);
    const [emitHeartbeat, setEmitHeartbeat] = useState(false);
    const [enableStreamRates, setEnableStreamRates] = useState(false);
    const [gcsRequestRates, setGcsRequestRates] = useState({ ...defaultStreamRates });
    const [ardupilotRates, setArdupilotRates] = useState({});
    const [ardupilotChannels, setArdupilotChannels] = useState([]);
    const [streamRateDocsOpen, setStreamRateDocsOpen] = useState(false);
    const [linkOverview] = useState({ sent: 0, received: 0, loss: 0, lostRate: 0 });

    useEffect(() => {
        const handler = (event, settings) => {
            setMavlinkSettings(settings);
            setGcsSystemId(settings.gcsSystemId);
            setEmitHeartbeat(settings.emitHeartbeat);
            setEnableStreamRates(settings.enableStreamRates);
            const requestSourceId = String(settings.streamSourceId ?? 0);
            const savedRequestRates = settings.streamRateSources?.[requestSourceId] || settings.streamRates;
            setGcsRequestRates(normalizeStreamRates(savedRequestRates));
        };
        window.api.getMavlinkSettings();
        window.api.onMavlinkSettings(handler);
        return () => {
            window.api.removeOnMavlinkSettings(handler);
        };
    }, []);

    const persistGcsSettings = (overrides = {}, requestRates = gcsRequestRates) => {
        const requestSourceId = String(mavlinkSettings.streamSourceId ?? 0);
        const normalizedRequestRates = normalizeStreamRates(requestRates);
        const nextRequestSources = {
            ...(mavlinkSettings.streamRateSources || {}),
            [requestSourceId]: normalizedRequestRates,
        };

        window.api.saveMavlinkSettings({
            ...mavlinkSettings,
            gcsSystemId,
            emitHeartbeat,
            enableStreamRates,
            ...overrides,
            streamSourceId: Number(requestSourceId),
            streamRateSources: nextRequestSources,
            streamRates: normalizedRequestRates,
        });
    };

    const handleSaveArdupilotRates = () => {
        const currentParams = (window.paramList && selectedMavId !== null && selectedMavId !== undefined)
            ? window.paramList[selectedMavId] || {}
            : {};
        const streamRateParamUpdates = buildArdupilotRateParamUpdates(
            ardupilotChannels,
            ardupilotRates,
            currentParams,
        );

        if (selectedMavId !== null && selectedMavId !== undefined) {
            window.api.setParams(selectedMavId, streamRateParamUpdates);
            if (window.paramList && window.paramList[selectedMavId]) {
                Object.keys(streamRateParamUpdates).forEach((paramName) => {
                    if (window.paramList[selectedMavId][paramName]) {
                        window.paramList[selectedMavId][paramName] = {
                            ...window.paramList[selectedMavId][paramName],
                            value: streamRateParamUpdates[paramName].value,
                        };
                    }
                });
                window.dispatchEvent(new CustomEvent('param-list', { detail: {} }));
            }
        }
    };

    const handleArdupilotRateChange = (sourceId, fieldName, rawValue) => {
        setArdupilotRates((prev) => {
            const sourceRates = normalizeStreamRates(prev[sourceId]);
            return {
                ...prev,
                [sourceId]: {
                    ...sourceRates,
                    [fieldName]: clampStreamRate(rawValue, sourceRates[fieldName]),
                },
            };
        });
    };

    const handleGcsRequestRateChange = (fieldName, rawValue) => {
        const nextRates = {
            ...gcsRequestRates,
            [fieldName]: clampStreamRate(rawValue, gcsRequestRates[fieldName]),
        };
        setGcsRequestRates(nextRates);
        persistGcsSettings({}, nextRates);
    };

    const handleGcsSystemIdChange = (rawValue) => {
        setGcsSystemId(rawValue);
        const nextSystemId = Number(rawValue);
        if (Number.isInteger(nextSystemId) && nextSystemId >= 1 && nextSystemId <= 255) {
            persistGcsSettings({ gcsSystemId: nextSystemId });
        }
    };

    const handleGcsSystemIdBlur = () => {
        const nextSystemId = Math.max(1, Math.min(255, Number(gcsSystemId) || 255));
        setGcsSystemId(nextSystemId);
        persistGcsSettings({ gcsSystemId: nextSystemId });
    };

    const handleEmitHeartbeatChange = (enabled) => {
        setEmitHeartbeat(enabled);
        persistGcsSettings({ emitHeartbeat: enabled });
    };

    const handleEnableStreamRatesChange = (enabled) => {
        setEnableStreamRates(enabled);
        persistGcsSettings({ enableStreamRates: enabled });
    };

    useEffect(() => {
        const params = (window.paramList && selectedMavId !== null && selectedMavId !== undefined)
            ? window.paramList[selectedMavId] || {}
            : {};
        const sourceDescriptors = buildArdupilotChannelDescriptors(params);
        const sortedSourceIds = sourceDescriptors.map((source) => source.id);
        setArdupilotChannels(sourceDescriptors);
        setArdupilotRates((prev) => {
            const next = mergeArdupilotRatesFromParams(sourceDescriptors, prev, params);
            sortedSourceIds.forEach((id) => {
                if (!next[id]) next[id] = { ...defaultStreamRates };
            });
            return next;
        });
    }, [selectedMavId, paramListTimestamp]);

    return (
        <Box
            sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                p: '2px',
                gap: '2px',
                boxSizing: 'border-box',
            }}
        >
            <Box sx={{ p: 2, pb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography variant="h6" sx={{ mr: 2 }}>Telemetry</Typography>
                </Stack>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    px: 2,
                    pb: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Box>
                    <SectionHeader title="Link Status" />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Stat label="Sent" value={linkOverview.sent} />
                        <Stat label="Received" value={linkOverview.received} />
                        <Stat label="Loss" value={linkOverview.loss} />
                        <Stat label="Lost Rate" value={linkOverview.lostRate} unit="%" />
                    </Box>
                </Box>

                <Box>
                    <SectionHeader title="General" />
                    <Box
                        sx={{
                            bgcolor: 'background.default',
                            borderRadius: 1,
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            <TextField
                                size="small"
                                sx={{ width: 160 }}
                                type="number"
                                label="GCS System ID"
                                inputProps={{ min: 1, max: 255 }}
                                value={gcsSystemId}
                                onChange={(e) => handleGcsSystemIdChange(e.target.value)}
                                onBlur={handleGcsSystemIdBlur}
                            />
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        size="small"
                                        checked={emitHeartbeat}
                                        onChange={(e) => handleEmitHeartbeatChange(e.target.checked)}
                                    />
                                )}
                                label="Emit Heartbeat"
                            />
                        </Stack>
                    </Box>
                </Box>

                <Box>
                    <SectionHeader
                        title="GCS Request Stream Rates"
                        actions={(
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        size="small"
                                        checked={enableStreamRates}
                                        onChange={(e) => handleEnableStreamRatesChange(e.target.checked)}
                                    />
                                )}
                                label="Enable Request"
                                sx={{ mr: 0 }}
                            />
                        )}
                    />
                    <Box
                        sx={{
                            bgcolor: 'background.default',
                            borderRadius: 1,
                            p: 2,
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                            gap: 1.25,
                        }}
                    >
                        {gcsRequestRateFields.map((field) => (
                            <TextField
                                size="small"
                                key={`gcs-${field.name}`}
                                type="number"
                                label={field.label}
                                disabled={!enableStreamRates}
                                inputProps={{ min: 0, max: 50, step: 1 }}
                                value={gcsRequestRates[field.name]}
                                onChange={(event) => handleGcsRequestRateChange(field.name, event.target.value)}
                            />
                        ))}
                    </Box>
                </Box>

                <Box>
                    <SectionHeader
                        title={(
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                <Box component="span">ArduPilot Channel Stream Rates</Box>
                                <Tooltip title="Stream rate parameter documentation">
                                    <IconButton
                                        size="small"
                                        aria-label="Open stream rate parameter documentation"
                                        onClick={() => setStreamRateDocsOpen(true)}
                                        sx={{ p: 0.25 }}
                                    >
                                        <DocumentationIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                        actions={(
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSaveArdupilotRates}
                                disabled={ardupilotChannels.length === 0}
                            >
                                Save
                            </Button>
                        )}
                    />
                    {ardupilotChannels.length === 0 ? (
                        <Box sx={{ bgcolor: 'background.default', borderRadius: 1, p: 2 }}>
                            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                No SRx stream-rate parameters were received from the selected vehicle.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {ardupilotChannels.map((source) => {
                                const sourceRates = ardupilotRates[source.id] || defaultStreamRates;
                                const fields = streamRateFields.filter((field) => source.fields.includes(field.name));
                                return (
                                    <Box
                                        key={source.id}
                                        sx={{
                                            bgcolor: 'background.default',
                                            borderRadius: 1,
                                            p: 2,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                mb: 1.5,
                                                fontSize: 13,
                                                fontWeight: 600,
                                                letterSpacing: 0.2,
                                            }}
                                        >
                                            {source.label} Parameters
                                            {source.interfaceLabel ? ` (${source.interfaceLabel})` : ''}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                                gap: 1.25,
                                            }}
                                        >
                                            {fields.map((field) => (
                                                <TextField
                                                    size="small"
                                                    key={`${source.id}-${field.name}`}
                                                    type="number"
                                                    label={field.label}
                                                    inputProps={{ min: 0, max: 50, step: 1 }}
                                                    id={`${source.id}-${field.name}`}
                                                    value={sourceRates[field.name]}
                                                    onChange={(event) => handleArdupilotRateChange(source.id, field.name, event.target.value)}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Box>

            <Dialog
                open={streamRateDocsOpen}
                onClose={() => setStreamRateDocsOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>ArduPilot Stream Rate Parameters</DialogTitle>
                <DialogContent dividers>
                    <Typography sx={{ mb: 2, color: 'text.secondary', fontSize: 13 }}>
                        Replace SRx with the channel name shown on this page, for example SR0 or SR1.
                        Message membership can vary slightly by vehicle type and firmware version.
                    </Typography>
                    <Stack spacing={1}>
                        {ardupilotStreamRateMessages.map(({ suffix, messages }) => (
                            <Box
                                key={suffix}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' },
                                    gap: 1,
                                    p: 1.25,
                                    bgcolor: 'background.default',
                                    borderRadius: 1,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                                        fontWeight: 600,
                                        fontSize: 13,
                                    }}
                                >
                                    {`SRx_${suffix}`}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                                        fontSize: 12,
                                        lineHeight: 1.6,
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {messages.join(', ')}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default TelemetryPage;
